import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { VideoRecorder } from "@/components/VideoRecorder";
import { AudioRecorder } from "@/components/AudioRecorder";
import { StarRating } from "@/components/StarRating";
import { Video, PenLine, Mic, Lock, ImagePlus, X as XIcon } from "lucide-react";
import { toast } from "sonner";

export default function RecordPage() {
  const { t, i18n } = useTranslation();
  const { slug } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [mode, setMode] = useState(null); // null | video | audio | text
  const [videoBlob, setVideoBlob] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [posterBlob, setPosterBlob] = useState(null);
  const [text, setText] = useState("");
  const [rating, setRating] = useState(5);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/public/campaigns/${slug}`).then((r) => setCampaign(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  // Force the campaign's own locale on the collection page (overrides visitor's preference)
  useEffect(() => {
    if (campaign?.locale && ["pt", "en"].includes(campaign.locale)) {
      if (i18n.language?.slice(0, 2) !== campaign.locale) {
        i18n.changeLanguage(campaign.locale);
      }
    }
  }, [campaign?.locale, i18n]);

  const canSubmit = consent && name.trim() && email.trim() && (mode === "text" ? text.trim() : (mode === "audio" ? audioBlob : videoBlob));

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) return toast.error(t("record.consentRequired"));
    if (!canSubmit) return toast.error(mode !== "text" && !videoBlob && !audioBlob ? t("record.recordFirst") : t("record.fillNameEmail"));
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("author_name", name);
      fd.append("author_email", email);
      fd.append("author_role", role);
      fd.append("type", mode);
      fd.append("text", text);
      fd.append("rating", rating);
      fd.append("consent", "true");
      if (avatarFile) fd.append("avatar", avatarFile);
      if (mode === "video" && videoBlob) {
        const ext = videoBlob.type.includes("mp4") ? "mp4" : "webm";
        fd.append("video", videoBlob, `testimonial.${ext}`);
        if (posterBlob) fd.append("poster", posterBlob, "poster.jpg");
      }
      if (mode === "audio" && audioBlob) {
        const ext = audioBlob.type.includes("mp4") ? "mp4" : "webm";
        fd.append("video", audioBlob, `testimonial.${ext}`);
      }
      await api.post(`/public/campaigns/${slug}/testimonials`, fd);
      navigate(`/c/${slug}/obrigado`);
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error(t("record.paused"));
      } else {
        toast.error(t("record.errorSend"));
      }
    } finally {
      setSending(false);
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6">
        <p data-testid="campaign-not-found" className="text-zinc-500 text-sm">{t("record.notFound")}</p>
      </div>
    );
  }
  if (!campaign) {
    return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center text-sm text-zinc-400">{t("common.loading")}</div>;
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <main className="max-w-lg mx-auto px-5 py-6 sm:py-10">
        <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight" data-testid="record-business-name">{campaign.business_name}</h1>
        {campaign.welcome_message && (
          <p className="text-zinc-600 mt-4 leading-relaxed" data-testid="welcome-message">{campaign.welcome_message}</p>
        )}

        {campaign.questions.length > 0 && (
          <div className="mt-6 bg-white border border-zinc-200 rounded-lg p-5">
            <p className="text-xs uppercase tracking-widest text-zinc-400 font-semibold mb-3">{t("record.fallSobre")}</p>
            <ul className="space-y-2">
              {campaign.questions.map((q, i) => (
                <li key={i} className="text-sm text-zinc-700 flex gap-2">
                  <span className="text-[#FF4F00] font-bold">{i + 1}.</span> {q}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-8">
          <p className="text-sm font-semibold mb-3">{t("record.askQuestion")}</p>
          {(campaign.accepting_text === false && campaign.accepting_media === false) && (
            <div data-testid="collection-paused-banner" className="mb-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <Lock size={14} /> {t("record.paused")}
            </div>
          )}
          <div className="grid grid-cols-3 gap-3">
            <button
              data-testid="mode-video-btn"
              onClick={() => campaign.accepting_media !== false && setMode("video")}
              disabled={campaign.accepting_media === false}
              className={`py-5 rounded-lg border-2 flex flex-col items-center gap-2 font-semibold text-sm transition-colors ${mode === "video" ? "border-[#FF4F00] bg-orange-50 text-[#FF4F00]" : "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-400"} disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-zinc-200`}
            >
              <Video size={22} /> {t("record.modeVideo")}
            </button>
            <button
              data-testid="mode-audio-btn"
              onClick={() => campaign.accepting_media !== false && setMode("audio")}
              disabled={campaign.accepting_media === false}
              className={`py-5 rounded-lg border-2 flex flex-col items-center gap-2 font-semibold text-sm transition-colors ${mode === "audio" ? "border-[#FF4F00] bg-orange-50 text-[#FF4F00]" : "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-400"} disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-zinc-200`}
            >
              <Mic size={22} /> {t("record.modeAudio")}
            </button>
            <button
              data-testid="mode-text-btn"
              onClick={() => campaign.accepting_text !== false && setMode("text")}
              disabled={campaign.accepting_text === false}
              className={`py-5 rounded-lg border-2 flex flex-col items-center gap-2 font-semibold text-sm transition-colors ${mode === "text" ? "border-[#FF4F00] bg-orange-50 text-[#FF4F00]" : "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-400"} disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-zinc-200`}
            >
              <PenLine size={22} /> {t("record.modeText")}
            </button>
          </div>
        </div>

        {mode === "video" && (
          <div className="mt-6">
            <VideoRecorder
              questions={campaign.questions}
              countdownSeconds={campaign.countdown_seconds ?? 3}
              onRecorded={(v, p) => { setVideoBlob(v); setPosterBlob(p); }}
            />
          </div>
        )}

        {mode === "audio" && (
          <div className="mt-6">
            <AudioRecorder
              countdownSeconds={campaign.countdown_seconds ?? 3}
              onRecorded={setAudioBlob}
            />
          </div>
        )}

        {mode === "text" && (
          <div className="mt-6">
            <textarea
              data-testid="testimonial-text-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={6}
              placeholder={t("record.textPh")}
              className="w-full px-4 py-3 bg-white border border-zinc-200 rounded-lg focus:ring-2 focus:ring-[#FF4F00] focus:outline-none text-sm resize-none"
            />
          </div>
        )}

        {mode && (
          <form onSubmit={submit} className="mt-8 bg-white border border-zinc-200 rounded-lg p-6 space-y-5">
            <div>
              <p className="text-sm font-semibold mb-2">{t("record.yourRating")}</p>
              <StarRating value={rating} onChange={setRating} />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1.5">{t("record.yourName")}</label>
              <input data-testid="author-name-input" value={name} onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-3 bg-[#F4F4F5] border-none rounded-md focus:ring-2 focus:ring-[#FF4F00] focus:outline-none text-sm" />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1.5">{t("record.yourEmail")}</label>
              <input data-testid="author-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-[#F4F4F5] border-none rounded-md focus:ring-2 focus:ring-[#FF4F00] focus:outline-none text-sm" />
              <p className="text-[11px] text-zinc-400 mt-1">{t("record.emailPrivate")}</p>
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1.5">{t("record.yourRole")}</label>
              <input data-testid="author-role-input" value={role} onChange={(e) => setRole(e.target.value)} placeholder={t("record.rolePh")}
                className="w-full px-4 py-3 bg-[#F4F4F5] border-none rounded-md focus:ring-2 focus:ring-[#FF4F00] focus:outline-none text-sm" />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1.5">{t("record.yourPhoto")}</label>
              <div className="flex items-center gap-3">
                {avatarPreview ? (
                  <div className="relative shrink-0">
                    <img src={avatarPreview} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-[#FF4F00]" data-testid="avatar-preview" />
                    <button
                      type="button"
                      data-testid="avatar-remove-btn"
                      onClick={() => { setAvatarFile(null); setAvatarPreview(null); }}
                      className="absolute -top-1 -right-1 w-5 h-5 bg-zinc-900 text-white rounded-full flex items-center justify-center hover:bg-zinc-700"
                      aria-label="Remover foto"
                    >
                      <XIcon size={12} />
                    </button>
                  </div>
                ) : (
                  <div className="w-16 h-16 rounded-full bg-zinc-100 border border-dashed border-zinc-300 flex items-center justify-center text-zinc-400 shrink-0">
                    <ImagePlus size={22} />
                  </div>
                )}
                <label className="flex-1 cursor-pointer">
                  <input
                    data-testid="avatar-input"
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      if (f.size > 5 * 1024 * 1024) return toast.error(t("record.photoTooLarge"));
                      setAvatarFile(f);
                      setAvatarPreview(URL.createObjectURL(f));
                    }}
                  />
                  <span className="inline-block px-4 py-2.5 border border-zinc-200 rounded-full text-xs font-semibold text-zinc-600 hover:border-zinc-400 transition-colors">
                    {avatarPreview ? t("record.changePhoto") : t("record.addPhoto")}
                  </span>
                  <p className="text-[11px] text-zinc-400 mt-1.5">{t("record.photoHint")}</p>
                </label>
              </div>
            </div>
            <label className="flex items-start gap-3 rounded-lg border border-zinc-200 bg-[#FAFAFA] p-3 cursor-pointer">
              <input
                data-testid="consent-checkbox"
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-[#FF4F00] shrink-0"
              />
              <span className="text-xs text-zinc-600 leading-relaxed">
                <span dangerouslySetInnerHTML={{ __html: t("record.consent", { business: campaign.business_name })
                  .replace("<a>", `<a href="/privacidade" target="_blank" class="text-[#FF4F00] font-semibold underline" style="color:#FF4F00;text-decoration:underline;font-weight:600">`)
                  .replace("</a>", "</a>")
                  .replace("<b>", `<a href="/termos" target="_blank" class="text-[#FF4F00] font-semibold underline" style="color:#FF4F00;text-decoration:underline;font-weight:600">`)
                  .replace("</b>", "</a>") }} />
              </span>
            </label>
            <button
              data-testid="submit-testimonial-btn"
              type="submit"
              disabled={sending || !consent}
              className="w-full py-4 bg-[#FF4F00] text-white rounded-full font-semibold hover:bg-[#E64600] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? t("common.sending") : t("record.submit")}
            </button>
          </form>
        )}
      </main>
    </div>
  );
}
